import { useTranslation } from 'react-i18next';
import type { PartnerCustomModelSummary } from '../../api.js';
import type { Tenant, TenantAssignment } from '../../types.js';
import { customCatalogModelId } from '../Partner.js';
import { renderCapabilityBadges, renderModelSpec } from '../model-capability-summary.js';

interface PartnerCustomModelDeployTabProps {
  customModels: PartnerCustomModelSummary[];
  ownedTenants: Tenant[];
  deployTenantId?: string;
  deployAssignment?: TenantAssignment;
  busy: boolean;
  onTenantChange: (tenantId?: string) => void;
  onDeploy: (modelKey: string) => void;
  onRevoke: (modelKey: string) => void;
}

export function PartnerCustomModelDeployTab({
  customModels,
  ownedTenants,
  deployTenantId,
  deployAssignment,
  busy,
  onTenantChange,
  onDeploy,
  onRevoke
}: PartnerCustomModelDeployTabProps) {
  const { t } = useTranslation();
  return (
    <section className="panel">
      <h3>{t('platform.customModelDeployment')}</h3>
      <div className="toolbar-form">
        <label>
          {t('tenantScope.deploymentTarget')}
          <select value={deployTenantId ?? ''} onChange={(event) => onTenantChange(event.target.value || undefined)}>
            <option value="">{t('tenantScope.selectTarget')}</option>
            {ownedTenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name} ({tenant.id})
              </option>
            ))}
          </select>
        </label>
      </div>
      {ownedTenants.length === 0 && <p className="muted">{t('platform.noPartnerTenants')}</p>}
      {deployTenantId && (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('partners.customModelKey')}</th>
              <th>{t('partners.customModelDisplayName')}</th>
              <th>{t('partners.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {customModels.map((model) => {
              const catalogModelId = customCatalogModelId(deployTenantId, model.modelKey);
              const deployed = Boolean(
                deployAssignment?.assignableModelIds?.includes(catalogModelId) ||
                  deployAssignment?.allowedModelIds.includes(catalogModelId)
              );
              return (
                <tr key={model.modelKey}>
                  <td>custom:{model.modelKey}</td>
                  <td>
                    <strong>{model.displayName}</strong>
                    <small>{renderModelSpec(t, model)}</small>
                    {renderCapabilityBadges(t, model)}
                  </td>
                  <td>{model.status}</td>
                  <td>
                    {deployed ? (
                      <button type="button" disabled={busy} onClick={() => onRevoke(model.modelKey)}>
                        {t('platform.revokeCustomModel')}
                      </button>
                    ) : (
                      <button type="button" disabled={busy || model.status !== 'active'} onClick={() => onDeploy(model.modelKey)}>
                        {t('platform.deployCustomModel')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {customModels.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  {t('partners.noCustomModels')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <p className="muted">{t('platform.customModelDeploymentHint')}</p>
    </section>
  );
}
